import { useRef, useState } from "react";
import { api } from "../api";
import type { LeadView } from "../types";

/**
 * Черновик письма клиенту.
 *
 * Сервис составляет его сам сразу после разбора разговора и присылает в
 * телеграм. Здесь тот же текст можно перечитать, скопировать целиком и
 * попросить составить заново — например, после правки карточки.
 */
export function LeadEmail({
  lead,
  onChanged,
}: {
  lead: LeadView;
  onChanged: (lead: LeadView) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const generate = async () => {
    setBusy(true);
    setError("");
    try {
      onChanged(await api.draftLeadEmail(lead.meeting_id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const text = `${lead.email_subject}\n\n${lead.email_body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена доступен не везде — тогда просто выделяем текст,
      // чтобы человек скопировал его сам.
      bodyRef.current?.select();
      setError("Скопируйте выделенный текст сами — браузер не дал доступ к буферу.");
    }
  };

  const has = lead.email_body.trim().length > 0;

  return (
    <>
      <h3>Письмо клиенту</h3>

      {error && <div className="banner red">{error}</div>}

      {!has ? (
        <div className="muted" style={{ marginBottom: 12 }}>
          <p style={{ margin: "0 0 12px" }}>
            Сервис может составить письмо по этому разговору: со ссылкой на то,
            о чём говорили, и одним понятным следующим шагом. Останется поправить
            пару строк и отправить.
          </p>
          <button className="primary" onClick={generate} disabled={busy}>
            {busy ? <span className="spinner" /> : null}
            {busy ? " Составляю…" : "Составить письмо"}
          </button>
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor={`subj-${lead.id}`}>Тема</label>
            <input id={`subj-${lead.id}`} value={lead.email_subject} readOnly />
          </div>

          <div className="field">
            <label htmlFor={`body-${lead.id}`}>Текст</label>
            <textarea
              id={`body-${lead.id}`}
              ref={bodyRef}
              value={lead.email_body}
              readOnly
              rows={Math.min(20, lead.email_body.split("\n").length + 2)}
            />
          </div>

          <div className="row">
            <button className="primary" onClick={copy}>
              {copied ? "Скопировано" : "Скопировать письмо"}
            </button>
            <button onClick={generate} disabled={busy}>
              {busy ? "Составляю…" : "Составить заново"}
            </button>
          </div>

          <div className="muted small" style={{ marginTop: 10 }}>
            Письмо составила модель по расшифровке разговора — перечитайте перед
            отправкой. Правьте уже в почте: здесь текст хранится как черновик.
            {lead.email_generated_at &&
              ` Составлено ${new Date(lead.email_generated_at).toLocaleString("ru-RU")}.`}
          </div>
        </>
      )}
    </>
  );
}
